import yaml from 'js-yaml';

/**
 * Shared model for authoring and rendering questionnaire translations.
 *
 * Translations are stored on the questionnaire as a locale-keyed blob:
 *
 *   { <locale>: {
 *       questions: { <questionRef>: { heading, sub_heading, body, placeholder,
 *                                     options: { <optionValue>: label },
 *                                     strings: { <builtinKey>: text } } },
 *       chrome: { <chromeKey>: text },
 *   } }
 *
 * Question keys are the client ref (which equals the persisted id once saved),
 * option labels are keyed by their stable value, and any missing or blank
 * string falls back to the English base content at render time.
 *
 * Pre-built steps (gender, date of birth, acknowledgment, clinical site) also
 * contribute their fixed interface copy: gender's standard options when the
 * question has none yet, the acknowledgment confirmation as an option, and
 * the age and site labels under `strings`.
 */

// Question text columns an admin can translate, paired with the storage key
// (snake_case, matching the backend) and the client field they mirror.
export const QUESTION_TEXT_FIELDS = [
  { client: 'heading', key: 'heading', label: 'Question text' },
  { client: 'subHeading', key: 'sub_heading', label: 'Subheading' },
  { client: 'body', key: 'body', label: 'Body / help text' },
  { client: 'placeholder', key: 'placeholder', label: 'Placeholder' },
];

// Shared interface chrome strings, rendered around every questionnaire. English
// is the authored source; each entry can be translated per locale.
export const INTERFACE_TEXT_CATALOG = [
  { key: 'progress', english: 'Question %{current} of %{total}' },
  { key: 'back', english: 'Back' },
  { key: 'next', english: 'Next' },
  { key: 'exit', english: 'Exit' },
  { key: 'continue', english: 'Continue' },
  { key: 'home', english: 'Home' },
  { key: 'other', english: 'Other' },
  { key: 'default_end_heading', english: 'Thanks for your time' },
  { key: 'default_completion_heading', english: 'Thank you!' },
  { key: 'load_error', english: 'Something went wrong loading your questionnaire:' },
];

const isBlank = (value) => value === undefined || value === null || String(value).trim() === '';

// Standard gender choices written onto a new gender question, and into the
// English catalog when a gender question has no options of its own yet.
export const GENDER_OPTION_DEFAULTS = [
  { value: 'Male', label: 'Male' },
  { value: 'Female', label: 'Female' },
  { value: 'Non-binary', label: 'Non-binary' },
  { value: 'Prefer not to answer', label: 'Prefer not to answer' },
];

// Fixed copy on pre-built steps that is not authored as question text. Stored
// under the question's `strings` map so each step's labels travel with it in
// the translation YAML.
const DATE_OF_BIRTH_STRINGS = [
  { key: 'date_placeholder', english: 'MM/DD/YYYY' },
  { key: 'month_invalid', english: 'Month must be between 1 and 12' },
  { key: 'day_invalid', english: 'Day must be between 1 and 31' },
  { key: 'impossible_date', english: "That day doesn't exist for that month" },
  { key: 'year_too_early', english: 'Year must be 1900 or later' },
  { key: 'future_date', english: "Date of birth can't be in the future" },
];

const YEAR_OF_BIRTH_STRINGS = [
  { key: 'year_placeholder', english: 'YYYY' },
  { key: 'year_too_early', english: 'Year must be 1900 or later' },
  { key: 'future_year', english: "Year of birth can't be in the future" },
];

const COMPONENT_STRINGS = {
  date_of_birth: DATE_OF_BIRTH_STRINGS,
  clinical_site: [
    { key: 'subject_id_label', english: 'Subject ID' },
    { key: 'subject_id_placeholder', english: 'Enter your Subject ID' },
    { key: 'site_label', english: 'Site' },
    { key: 'site_name_label', english: 'Site Name:' },
    { key: 'site_id_label', english: 'Site ID:' },
    { key: 'change_site', english: 'Change site' },
    { key: 'search_placeholder', english: 'Search by site name or Site ID' },
    { key: 'matching_sites', english: 'Matching sites' },
    { key: 'searching', english: 'Searching…' },
    { key: 'no_matches', english: 'No matching sites. Try a different name or Site ID.' },
    { key: 'subject_and_site_required', english: 'Please enter your Subject ID and select your Site.' },
    { key: 'subject_required', english: 'Please enter your Subject ID.' },
    { key: 'site_required', english: 'Please select your Site from the list.' },
    {
      key: 'site_not_found',
      english:
        "The selected Site was not found in this study's site list. Please search again and choose a site from the list.",
    },
  ],
};

// Built-in choices that are not stored as question options. The acknowledgment
// confirmation is the checkbox the member checks; it is keyed like any other
// option so it shows up under `options` in the YAML.
const COMPONENT_OPTIONS = {
  acknowledgment: [{ key: 'confirmation', value: 'acknowledged', english: 'Yes, I understand' }],
};

const componentStringRows = (question) => {
  if (question.componentType === 'date_of_birth' && question.yearOfBirth) return YEAR_OF_BIRTH_STRINGS;

  return COMPONENT_STRINGS[question.componentType] || [];
};

const builtinOptionRows = (question, optionRows) => {
  if (question.componentType === 'gender' && optionRows.length === 0) {
    return GENDER_OPTION_DEFAULTS.map((option) => ({ value: option.value, english: option.label }));
  }

  return (COMPONENT_OPTIONS[question.componentType] || [])
    .filter((option) => !optionRows.some((existing) => existing.value === option.value))
    .map((option) => ({ value: option.value, english: option.english }));
};

// English defaults for one pre-built step's fixed copy, keyed for the component
// that renders it (acknowledgment confirmation, date messages, site labels).
export const defaultComponentCopy = (componentType, options = {}) => {
  const copy = {};
  const stringFields =
    componentType === 'date_of_birth' && options.yearOfBirth
      ? YEAR_OF_BIRTH_STRINGS
      : COMPONENT_STRINGS[componentType] || [];

  stringFields.forEach((field) => {
    copy[field.key] = field.english;
  });

  (COMPONENT_OPTIONS[componentType] || []).forEach((option) => {
    copy[option.key] = option.english;
  });

  return copy;
};

// Resolves a pre-built step's fixed copy for a locale, falling back per string
// to English. Acknowledgment confirmation is read from the option labeled
// `acknowledged`; the rest come from the question's `strings` map.
export const componentCopy = (question, translations, locale) => {
  const copy = defaultComponentCopy(question.componentType, { yearOfBirth: question.yearOfBirth });
  if (!locale || locale === 'en' || !translations || !translations[locale]) return copy;

  const entry = (translations[locale].questions || {})[question.ref] || {};
  const strings = entry.strings || {};
  const optionLabels = entry.options || {};

  Object.keys(copy).forEach((key) => {
    const builtinOption = (COMPONENT_OPTIONS[question.componentType] || []).find((option) => option.key === key);
    const translated = builtinOption ? optionLabels[builtinOption.value] : strings[key];
    copy[key] = localizedString(copy[key], translated);
  });

  return copy;
};

// Swaps a server error for its translation when it matches a built-in English
// string on this step (for example the clinical site "not found" message).
export const translateKnownMessage = (question, message, translations, locale) => {
  if (!question || isBlank(message)) return message;

  const defaults = defaultComponentCopy(question.componentType);
  const key = Object.keys(defaults).find((candidate) => defaults[candidate] === message);
  if (!key) return message;

  return componentCopy(question, translations, locale)[key];
};

// The English strings on a single question that can be translated: the text
// columns that actually have content, one row per answer option (including
// built-in options such as the acknowledgment confirmation), and the fixed
// copy for pre-built steps.
export const questionTranslatableRows = (question) => {
  const textRows = QUESTION_TEXT_FIELDS.filter((field) => !isBlank(question[field.client])).map((field) => ({
    key: field.key,
    label: field.label,
    english: question[field.client],
  }));

  const authoredOptions = (question.options || [])
    .filter((option) => !isBlank(option.label))
    .map((option) => ({ value: option.value, english: option.label }));

  const optionRows = [...authoredOptions, ...builtinOptionRows(question, authoredOptions)];
  const stringRows = componentStringRows(question);

  return { textRows, optionRows, stringRows };
};

// Total translatable strings across the questionnaire (question text + option
// labels + interface chrome), used as the denominator for progress.
export const totalTranslatableStrings = (questions) => {
  const questionTotal = questions.reduce((sum, question) => {
    const { textRows, optionRows, stringRows } = questionTranslatableRows(question);
    return sum + textRows.length + optionRows.length + stringRows.length;
  }, 0);

  return questionTotal + INTERFACE_TEXT_CATALOG.length;
};

// Count of non-blank translated strings an admin has entered for one locale.
export const translatedStringCount = (localeTranslations, questions) => {
  if (!localeTranslations) return 0;

  const questionData = localeTranslations.questions || {};
  const questionCount = questions.reduce((sum, question) => {
    const entry = questionData[question.ref] || {};
    const { textRows, optionRows, stringRows } = questionTranslatableRows(question);

    const textFilled = textRows.filter((row) => !isBlank(entry[row.key])).length;
    const optionsEntry = entry.options || {};
    const optionsFilled = optionRows.filter((row) => !isBlank(optionsEntry[row.value])).length;
    const stringsEntry = entry.strings || {};
    const stringsFilled = stringRows.filter((row) => !isBlank(stringsEntry[row.key])).length;

    return sum + textFilled + optionsFilled + stringsFilled;
  }, 0);

  const chromeData = localeTranslations.chrome || {};
  const chromeCount = INTERFACE_TEXT_CATALOG.filter((item) => !isBlank(chromeData[item.key])).length;

  return questionCount + chromeCount;
};

// Number of strings still untranslated for a locale.
export const missingTranslationCount = (localeTranslations, questions) =>
  totalTranslatableStrings(questions) - translatedStringCount(localeTranslations, questions);

// Resolves a localized string for the mentee-facing flow, falling back to the
// English base whenever the translation is missing or blank.
export const localizedString = (english, translated) => (isBlank(translated) ? english : translated);

// English defaults for every interface chrome key, used as the fallback for
// any locale that has not translated a given string.
export const INTERFACE_TEXT_DEFAULTS = INTERFACE_TEXT_CATALOG.reduce((defaults, item) => {
  defaults[item.key] = item.english;
  return defaults;
}, {});

// Replaces %{name} placeholders in an interface string (e.g. the progress
// counter "Question %{current} of %{total}").
export const interpolate = (template, values) =>
  String(template).replace(/%\{(\w+)\}/g, (match, key) => (values[key] === undefined ? match : String(values[key])));

// Resolves an interface chrome string for a locale, falling back to English.
export const chromeString = (translations, locale, key) => {
  const fallback = INTERFACE_TEXT_DEFAULTS[key] || '';
  if (!locale || locale === 'en' || !translations || !translations[locale]) return fallback;

  const chrome = translations[locale].chrome || {};
  return localizedString(fallback, chrome[key]);
};

// Overlays a locale's translated strings onto a question's English content,
// keeping every answer value stable and falling back per-string to English.
export const localizeQuestion = (question, translations, locale) => {
  if (!locale || locale === 'en' || !translations || !translations[locale]) return question;

  const entry = (translations[locale].questions || {})[question.ref];
  if (!entry) return question;

  const optionLabels = entry.options || {};
  return {
    ...question,
    heading: localizedString(question.heading, entry.heading),
    subHeading: localizedString(question.subHeading, entry.sub_heading),
    body: localizedString(question.body, entry.body),
    placeholder: localizedString(question.placeholder, entry.placeholder),
    options: (question.options || []).map((option) => ({
      ...option,
      label: localizedString(option.label, optionLabels[option.value]),
    })),
  };
};

// Picks the best enabled locale for a set of preferred browser languages,
// matching an exact tag first (fr-CA) then the base language (fr). Returns
// 'en' when nothing matches, so the English base content is shown.
export const detectLocale = (preferredLanguages, enabledLocales) => {
  const preferred = Array.isArray(preferredLanguages) ? preferredLanguages : [preferredLanguages];

  for (let index = 0; index < preferred.length; index += 1) {
    const language = preferred[index];
    if (isBlank(language)) continue;

    if (enabledLocales.includes(language)) return language;

    const base = String(language).split('-')[0];
    const match = enabledLocales.find((locale) => locale === base || locale.split('-')[0] === base);
    if (match) return match;
  }

  return 'en';
};

// The English base strings for the whole questionnaire, shaped the same way as
// stored translations: { chrome: { key: text }, questions: { ref: { field: text,
// options: { value: text } } } }. Drives both YAML generation and parsing.
export const buildEnglishCatalog = (questions) => {
  const chrome = {};
  INTERFACE_TEXT_CATALOG.forEach((item) => {
    chrome[item.key] = item.english;
  });

  const questionsMap = {};
  questions.forEach((question) => {
    const { textRows, optionRows, stringRows } = questionTranslatableRows(question);
    if (textRows.length === 0 && optionRows.length === 0 && stringRows.length === 0) return;

    const entry = {};
    textRows.forEach((row) => {
      entry[row.key] = row.english;
    });
    if (optionRows.length > 0) {
      entry.options = {};
      optionRows.forEach((row) => {
        entry.options[row.value] = row.english;
      });
    }
    if (stringRows.length > 0) {
      entry.strings = {};
      stringRows.forEach((row) => {
        entry.strings[row.key] = row.english;
      });
    }
    questionsMap[question.ref] = entry;
  });

  return { chrome, questions: questionsMap };
};

// Quotes a YAML key the same way js-yaml would, including numeric refs like '23'
// and option values that contain spaces or punctuation.
const yamlKey = (key) => {
  const dumped = yaml.safeDump({ [String(key)]: '' }, { lineWidth: -1 }).replace(/\n$/, '');
  const suffix = ": ''";
  return dumped.endsWith(suffix) ? dumped.slice(0, -suffix.length) : String(key);
};

// One double-quoted line, so a source comment can sit beside multiline text
// without the comment becoming part of the value.
const yamlQuoted = (value) =>
  `"${String(value)
    .replace(/\\/g, '\\\\')
    .replace(/"/g, '\\"')
    .replace(/\n/g, '\\n')
    .replace(/\r/g, '\\r')
    .replace(/\t/g, '\\t')}"`;

const englishComment = (english) => ` # ${String(english).replace(/\r\n/g, '\n').replace(/\n/g, '\\n')}`;

const translationLine = (indent, key, value, english) =>
  `${' '.repeat(indent)}${yamlKey(key)}: ${yamlQuoted(isBlank(value) ? '' : value)}${englishComment(english)}`;

// A translation counts only when a scalar was supplied. Blank lines fall back
// to English. A value that matches the English still counts: words like
// "Neutral" are real translations.
const suppliedTranslation = (value) => {
  if (value === undefined || value === null || typeof value === 'object') return null;

  const text = String(value);
  return text.trim() === '' ? null : text;
};

// Renders a locale's translations as an editable YAML document. Missing
// translations are empty strings; the English source sits in a comment so a
// translator can fill the value in, including with the same word.
export const buildLocaleYaml = (locale, englishCatalog, localeTranslations) => {
  const stored = localeTranslations || {};
  const storedChrome = stored.chrome || {};
  const storedQuestions = stored.questions || {};
  const lines = [`${yamlKey(locale)}:`, '  chrome:'];

  Object.entries(englishCatalog.chrome).forEach(([key, english]) => {
    lines.push(translationLine(4, key, storedChrome[key], english));
  });

  lines.push('  questions:');
  const questionRefs = Object.keys(englishCatalog.questions);
  if (questionRefs.length === 0) {
    lines[lines.length - 1] = '  questions: {}';
  }

  questionRefs.forEach((ref) => {
    const fields = englishCatalog.questions[ref];
    const storedEntry = storedQuestions[ref] || {};
    const storedOptions = storedEntry.options || {};
    const storedStrings = storedEntry.strings || {};

    lines.push(`    ${yamlKey(ref)}:`);
    Object.entries(fields).forEach(([key, english]) => {
      if (key === 'options' || key === 'strings') return;
      lines.push(translationLine(6, key, storedEntry[key], english));
    });

    if (fields.options) {
      lines.push('      options:');
      Object.entries(fields.options).forEach(([value, english]) => {
        lines.push(translationLine(8, value, storedOptions[value], english));
      });
    }

    if (fields.strings) {
      lines.push('      strings:');
      Object.entries(fields.strings).forEach(([key, english]) => {
        lines.push(translationLine(8, key, storedStrings[key], english));
      });
    }
  });

  return `${lines.join('\n')}\n`;
};

// Parses a (possibly AI-translated) YAML document back into stored translations
// for one locale. Only keys present in the English catalog are kept, unknown
// keys are ignored, and any non-blank value is kept, including one that matches
// the English source. Blank values are dropped so they fall back at render time.
// Throws when the YAML is invalid.
export const parseLocaleYaml = (text, locale, englishCatalog) => {
  const parsed = yaml.safeLoad(text, { schema: yaml.CORE_SCHEMA });
  if (!parsed || typeof parsed !== 'object') {
    throw new Error('The YAML did not contain any translations.');
  }

  // Accept either a locale-wrapped document ({ fr: { chrome, questions } }) or a
  // bare one ({ chrome, questions }), in case the top-level key is dropped.
  const root = parsed[locale] && typeof parsed[locale] === 'object' ? parsed[locale] : parsed;
  const chromeIn = root.chrome || {};
  const questionsIn = root.questions || {};
  const stringMap = (value) => (value && typeof value === 'object' ? value : {});

  const chrome = {};
  Object.keys(englishCatalog.chrome).forEach((key) => {
    const value = suppliedTranslation(chromeIn[key]);
    if (value !== null) chrome[key] = value;
  });

  const questions = {};
  Object.entries(englishCatalog.questions).forEach(([ref, fields]) => {
    const inEntry = questionsIn[ref] || {};
    const inOptions = stringMap(inEntry.options);
    const inStrings = stringMap(inEntry.strings);
    const entry = {};

    Object.keys(fields).forEach((key) => {
      if (key === 'options' || key === 'strings') return;
      const value = suppliedTranslation(inEntry[key]);
      if (value !== null) entry[key] = value;
    });

    if (fields.options) {
      const options = {};
      Object.keys(fields.options).forEach((value) => {
        const translated = suppliedTranslation(inOptions[value]);
        if (translated !== null) options[value] = translated;
      });
      if (Object.keys(options).length > 0) entry.options = options;
    }

    if (fields.strings) {
      const strings = {};
      Object.keys(fields.strings).forEach((key) => {
        const translated = suppliedTranslation(inStrings[key]);
        if (translated !== null) strings[key] = translated;
      });
      if (Object.keys(strings).length > 0) entry.strings = strings;
    }

    if (Object.keys(entry).length > 0) questions[ref] = entry;
  });

  const result = {};
  if (Object.keys(chrome).length > 0) result.chrome = chrome;
  if (Object.keys(questions).length > 0) result.questions = questions;
  return result;
};
